import { useCallback, useState } from 'react';
import styles from '../ScenarioGenerator.module.css';
import msStyles from '../../../common/MultiSelectDropdown.module.css';
import { Tooltip } from '../../../common/Tooltip';
import { HelpTooltip } from '../../../common/HelpTooltip';
import { HierarchicalSelect } from '../../../common/HierarchicalSelect';
import { SingleSelectDropdown } from '../../../common/SingleSelectDropdown';
import { ChevronIcon } from '../../../common/selectIcons';
import { ScenarioModelDropdownPortal } from '../ScenarioModelDropdownPortal';
import { videoModelGroupLabel, videoModelButtonLabel } from '../../common/VideoModelFamilySelector';
import { usePortalDropdown } from '../hooks/usePortalDropdown';
import { SCENARIO_MODEL_RANDOM } from '../../state/useGenerationState';
import { useGenerationSettings } from '../../state/settingsContext';
import { useScenarioAttributesState } from '../../state/scenarioAttributesContext';
import type { SetupPanelProps } from '../SetupPanel';
import { useScenarioSetup } from '../hooks/ScenarioSetupContext';
import { normalizeVisualStyle } from '../../state/useGenerationState';
import type { HierarchicalOption } from '../../../common/HierarchicalSelect';
import { isDurationBelowLyricsMin } from '../lyricsDurationGate';
import {
  CUSTOM_DURATION_MAX_SECONDS, CUSTOM_DURATION_MIN_SECONDS, CUSTOM_DURATION_OPTION_VALUE,
  clampCustomDurationSeconds, customSecondsToDurationValue, isCustomDurationValue, parseCustomDurationValue, resolveDurationOption,
} from '../customDuration';
import {
  MOTION_ENGINE, MOTION_DURATION_TOO_LONG_MESSAGE, filterDurationOptionsForMotion, filterScenarioCountOptionsForMotion, isDurationOverMotionWholeMax,
} from '../motionScenario';
import { MOTION_GRAPHICS_WHOLE, effectiveMotionGraphics, motionGraphicsFullLabel } from '../../state/motionGraphics';
import { MOTION_VOICE_TOGGLE_OPTIONS, deriveMotionVoiceToggle, voiceToBehaviorValue, type MotionVoiceToggleValue } from '../motionVoiceToggle';
import { PROMPT_HISTORY_KEYS } from '../../../../utils/promptHistory';
import { PromptHistoryModal } from '../../../common/PromptHistoryModal';
import { ScenarioScriptImportCard, ScenarioScriptImportEntry } from './ScenarioScriptImportEntry';
import scriptImportStyles from './ScenarioScriptImport.module.css';
import {
  SCRIPT_FOLLOW_LABEL, SCRIPT_FOLLOW_PENDING_LABEL, SCRIPT_IMPORT_COUNT_REASON, SCRIPT_IMPORT_DIALECT_REASON,
  SCRIPT_IMPORT_FIELD_REASON,
} from '../scriptImport';
// 履歴パネルの共通スタイル (静止画・プロンプトモードと同じ)。ルート分割でも確実に載るよう
// このコンポーネント側でも import する。
import '../../../../styles/prompt-history.css';

// 映像スタイルの選択肢を系統別グループにまとめる (HierarchicalSelect がモーダル表示)。
const VISUAL_STYLE_HIERARCHICAL_OPTIONS: HierarchicalOption[] = [
  {
    id: 'vs-grp-photoreal', key: 'vs-grp-photoreal', label: '実写系', type: 'group',
    children: [
      { id: 'photorealistic', key: 'photorealistic', label: '実写風（標準）', type: 'option' },
      { id: 'cinematic', key: 'cinematic', label: 'シネマティック実写', type: 'option' },
      { id: 'documentary', key: 'documentary', label: 'ドキュメンタリー実写', type: 'option' },
      { id: 'retro_film', key: 'retro_film', label: 'レトロフィルム調', type: 'option' },
    ],
  },
  {
    id: 'vs-grp-anime', key: 'vs-grp-anime', label: 'アニメ・イラスト系', type: 'group',
    children: [
      { id: 'anime', key: 'anime', label: 'アニメ風（2D）', type: 'option' },
      { id: 'anime_3d', key: 'anime_3d', label: '3Dアニメ風', type: 'option' },
      { id: 'watercolor', key: 'watercolor', label: '水彩・絵本タッチ', type: 'option' },
      { id: 'cartoon', key: 'cartoon', label: 'カートゥーン／アメコミ調', type: 'option' },
      { id: 'pixel_art', key: 'pixel_art', label: 'ピクセルアート', type: 'option' },
    ],
  },
  {
    id: 'vs-grp-graphic', key: 'vs-grp-graphic', label: '非写実・グラフィック系', type: 'group',
    children: [
      { id: 'clay', key: 'clay', label: 'クレイ／ストップモーション風', type: 'option' },
      { id: 'paper_craft', key: 'paper_craft', label: 'ペーパークラフト／切り絵風', type: 'option' },
      { id: 'motion_graphics', key: 'motion_graphics', label: 'モーショングラフィックス', type: 'option' },
    ],
  },
];

export function ScenarioSettingsSection(props: SetupPanelProps) {
  const { accordions } = props;

  const {
    scenarioCount, setScenarioCount, scenarioCountOptions,
    duration, tone, onToneChange, onVisualStyleChange, toneHierarchicalOptions,
    durationSelectOptions: durationOptions,
    modelAwareDurationDefault, modelMaxDuration, selectedVideoModelDef, onDurationChange,
    isLyricsTone, isMotionEngine,
    videoGenerationMode,
    toneRecommendedKeys, visualStyleRecommendedKeys, motionGraphicsRecommendedKeys, recommendedDuration,
    includeNarration, setIncludeNarration, includeTelop, setIncludeTelop, includeDialogue, setIncludeDialogue,
    narrationTreeOptions, narrationTreeEnabled, narrationCategoryEnabled,
    dialogueTreeOptions, dialogueTreeEnabled, dialogueCategoryEnabled,
    telopTreeOptions, telopTreeEnabled, telopCategoryEnabled,
    visualStyle,
    visualStyleHierarchicalOptions, visualStyleTreeEnabled, visualStyleIsStylized, visualStyleFieldPlacement,
    additionalPrompt, setAdditionalPrompt,
    scenarioSettingsSummary,
    scenarioModel, setScenarioModel, scenarioModelDefs, randomRateBadge, currentScenarioModelDef, selectedScenarioModelRate,
    randomCreditText,
    DEFAULT_SCENARIO_MODEL,
    scenarioFieldConfig,
    importedScript } = useScenarioSetup();

  // 台本取り込み中 (§4.2)。全編モーションとは併用できないため、モーション系の映像スタイルでは通常表示に戻す
  // (取り込みブロック側で非活性 + 理由を出し、送信も止める)。
  const isScriptImport = !!importedScript && !isMotionEngine;
  // 取り込み中はシナリオ数を1案として扱う (保存済みの案数は書き換えない)
  const displayScenarioCount = importedScript ? 1 : scenarioCount;

  // 取り込み中に「台本に従う」を表示する非活性フィールド。トリガーは押せないため、ホバーはラッパーで受けて理由を出す。
  const scriptFixedField = (
    testId: string,
    label: string,
    icon: string,
    valueLabel: string,
    reason: string,
    extra?: React.ReactNode,
  ) => (
    <div className={styles.optionGroup} data-testid={testId}>
      <div className="gen-option-group-label" style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
        <img src={icon} alt="" aria-hidden="true" className={styles.optionLabelIcon} />
        {label}
      </div>
      <Tooltip content={reason}>
        <div style={{ pointerEvents: 'none' }}>
          <SingleSelectDropdown
            label={label}
            options={[{ value: 'script', label: valueLabel }]}
            value="script"
            onChange={() => {}}
            disabled
            labelOnly
            fitContent
            alwaysShowSelected
            className={`${styles.dropdownFit} ${styles.scenarioDropdown}`}
            dropdownClassName={styles.dropdownNoWrap}
          />
        </div>
      </Tooltip>
      {extra}
    </div>
  );

  // 追加プロンプトの履歴 (全画面モーダル。docs/features/prompt-history-db-plan.md)。
  // 保存は生成実行時 (useScenarioGeneration) が行い、ここは表示と選択のみ。
  const [showAdditionalPromptHistory, setShowAdditionalPromptHistory] = useState(false);

  const handleToggleAdditionalPromptHistory = useCallback(() => {
    setShowAdditionalPromptHistory((prev) => !prev);
  }, []);

  const handleSelectAdditionalPromptHistory = useCallback((prompt: string) => {
    setAdditionalPrompt(prompt);
  }, [setAdditionalPrompt]);

  // シナリオ生成AIモデルのポータルドロップダウン (動画モデル選択と共通スタイル)
  const scenarioModelDropdown = usePortalDropdown({
    triggerClassName: styles.scenarioModelTrigger,
  });

  const {
    isScenarioSettingsOpen, setIsScenarioSettingsOpen,
    isScenarioAdvancedOpen, setIsScenarioAdvancedOpen } = accordions;

  const {
    shootingMethod, setShootingMethod, dialect, setDialect, motionGraphics, setMotionGraphics,
    targetGender, setTargetGender, targetAge, setTargetAge,
  } = useGenerationSettings();
  const {
    shootingMethodOptions, shootingMethodFieldPlacement, dialectOptions, dialectFieldPlacement,
    motionGraphicsOptions, motionGraphicsFieldPlacement,
    targetGenderOptions, targetGenderFieldPlacement, targetAgeOptions, targetAgeFieldPlacement,
  } = useScenarioAttributesState();
  // ターゲット性別・年代 (属性ツリー target_gender / target_age)。指定なしはプロンプトに何も足さない。
  // ツリーに field が無い環境 (migration 未適用) では出さない。台本取り込み中も訴求・登場人物の指示として送る
  const targetGenderBlock = targetGenderOptions.length > 0 ? (
    <div className={styles.optionGroup} data-testid="target-gender-field">
      <HierarchicalSelect
        label="ターゲット性別"
        icon="/venus_and_mars.svg"
        options={[
          { id: 'target-gender-none', key: 'none', label: '指定なし', type: 'option' },
          ...targetGenderOptions,
        ]}
        value={targetGender || 'none'}
        onChange={key => setTargetGender(key === 'none' ? '' : key)}
        noneLabel="指定なし"
      />
    </div>
  ) : null;
  const targetAgeBlock = targetAgeOptions.length > 0 ? (
    <div className={styles.optionGroup} data-testid="target-age-field">
      <HierarchicalSelect
        label="ターゲット年代"
        icon="/cursor_target_1.svg"
        options={[
          { id: 'target-age-none', key: 'none', label: '指定なし', type: 'option' },
          ...targetAgeOptions,
        ]}
        value={targetAge || 'none'}
        onChange={key => setTargetAge(key === 'none' ? '' : key)}
        noneLabel="指定なし"
      />
    </div>
  ) : null;
  // 方言 (セリフ・ナレーションの話し言葉)。指定なしはプロンプトに何も足さない。
  // 台本取り込み中はセリフを書き換えないため方言を適用しない (§6.4。送信もしない)
  const dialectBlock = dialectOptions.length > 0 ? (isScriptImport ? scriptFixedField(
    'script-import-dialect', '方言', '/messages_people_person_bubble_circle_1.svg', SCRIPT_FOLLOW_LABEL, SCRIPT_IMPORT_DIALECT_REASON,
  ) : (
    <div className={styles.optionGroup}>
      <HierarchicalSelect
        label="方言"
        icon="/messages_people_person_bubble_circle_1.svg"
        options={[
          { id: 'dialect-none', key: 'none', label: '指定なし', type: 'option' },
          ...dialectOptions,
        ]}
        value={dialect || 'none'}
        onChange={key => setDialect(key === 'none' ? '' : key)}
        noneLabel="指定なし"
      />
    </div>
  )) : null;
  const shootingMethodBlock = shootingMethodOptions.length > 0 ? (
    <div className={styles.optionGroup}>
      <HierarchicalSelect
        label="撮影方法"
        icon="/camera_video.svg"
        options={[
          { id: 'shooting-method-none', key: 'none', label: '指定なし', type: 'option' },
          ...shootingMethodOptions,
        ]}
        value={shootingMethod || 'none'}
        onChange={key => setShootingMethod(key === 'none' ? '' : key)}
        noneLabel="指定なし"
      />
    </div>
  ) : null;

  // 「モーショングラフィックス」の3択 (計画 md §10.14)。映像スタイルと並ぶ独立の項目で、ツリーに field が無い環境
  // (migration 未適用) では出さない。プルダウンは短縮名、ツールチップは正式名 (metadata.full_label)。
  // 全編 (whole) のときは映像スタイル欄を隠す (全編では映像スタイルに意味が無い。値は保持して whole を外せば戻る)
  const motionGraphicsValue = effectiveMotionGraphics(motionGraphics, motionGraphicsOptions);
  const hideVisualStyle = motionGraphicsValue === MOTION_GRAPHICS_WHOLE;
  const motionGraphicsBlock = motionGraphicsOptions.length > 0 ? (
    <div className={styles.optionGroup} data-testid="motion-graphics-field">
      <HierarchicalSelect
        label="モーショングラフィックス"
        icon="/scene_motion.svg"
        options={[
          { id: 'motion-graphics-none', key: 'none', label: '指定なし', type: 'option' },
          ...motionGraphicsOptions,
        ]}
        value={motionGraphicsValue || 'none'}
        onChange={key => setMotionGraphics(key === 'none' ? '' : key)}
        noneLabel="指定なし"
        recommendedKeys={motionGraphicsRecommendedKeys}
        optionIcon={opt => (opt.key === MOTION_GRAPHICS_WHOLE ? '/scene_motion.svg' : undefined)}
        optionTooltip={opt => (opt.key === 'none' ? undefined : motionGraphicsFullLabel(opt))}
      />
      {hideVisualStyle && (
        <div style={{ fontSize: '11px', color: 'rgba(245, 241, 230, 0.5)', marginTop: '4px', maxWidth: 220 }}>
          全編モーションでは実写の人間AI俳優・Seedanceモデルキャラクターは選べません（キャラクター俳優は選択できます）。
        </div>
      )}
    </div>
  ) : null;

  // 映像スタイルの選択肢アイコン: モーショングラフィックス (metadata.production_engine === 'motion') の頭に
  // 生成方法タブ・設定アコーディオンと同じ scene_motion.svg を出す。
  const visualStyleOptionIcon = (opt: HierarchicalOption) =>
    opt.metadata?.production_engine === MOTION_ENGINE ? '/scene_motion.svg' : undefined;

  // 歌モノのトーンには、上を向いて歌う横顔と音符の専用アイコンを出す。
  const toneOptionIcon = (opt: HierarchicalOption) =>
    opt.metadata?.bgm_mode === 'lyrics' ? '/scenario_song.svg' : undefined;

  // 映像スタイル選択ブロック。フィールドの placement に応じて主表示 or 詳細設定内に配置する。
  const visualStyleBlock = hideVisualStyle ? null : (
    <div className={styles.optionGroup} style={{ maxWidth: 200 }}>
      <HierarchicalSelect
        label="映像スタイル"
        icon="/movies_sit_drink.svg"
        options={visualStyleTreeEnabled && visualStyleHierarchicalOptions && visualStyleHierarchicalOptions.length > 0
          ? visualStyleHierarchicalOptions
          : VISUAL_STYLE_HIERARCHICAL_OPTIONS}
        value={visualStyle}
        onChange={(key) => {
          if (key === 'none') { onVisualStyleChange('photorealistic'); return; }
          // DB ツリー有効時は raw key を保持 (OEM 独自 key を潰さない)。無効時のみ正規化。
          // onVisualStyleChange は setVisualStyle + 推奨採用ログ (計画書§8 P4)。
          onVisualStyleChange(visualStyleTreeEnabled ? key : normalizeVisualStyle(key));
        }}
        noneLabel="デフォルトに戻す"
        defaultValue="photorealistic"
        recommendedKeys={visualStyleRecommendedKeys}
        optionIcon={visualStyleOptionIcon}
      />
      {visualStyleIsStylized && (
        <div style={{ fontSize: '11px', color: 'rgba(245, 241, 230, 0.5)', marginTop: '4px' }}>
          このスタイルでは実写の人間AI俳優・Seedanceモデルキャラクターは選べません（キャラクター俳優は選択できます）。
        </div>
      )}
    </div>
  );

  // ナレ/セリフ/テロップ(フェーズ6): tree 有効時はツリー option(会話/後付け含む)、無効時は
  // legacy option を出す。「指定なし」は両モードで先頭合成(DB ノードにしないため)。
  // 表示判定: tree 有効(=フラグ ON かつ effective tree に存在)→表示(ツリー可視性が正本。旧
  // show_* とは AND しない=ツリーUIで再表示すれば出る) / フラグ ON だが非存在(除外)→隠す /
  // フラグ OFF→legacy 表示フラグ。
  const UNSPECIFIED_OPT = { value: 'unspecified', label: '指定なし' };
  // legacyDefault=フラグ OFF 時の既定値。defaultMode=既定に相当する mode(tree 有効時はこの mode を
  // 持つ option key を既定とする)。defaultValue は黄色枠線(value!==default)の判定に使うため、
  // 「実際の初期値」と一致させる(既定のときは枠線を出さない)。
  const behaviorField = (
    treeEnabled: boolean,
    categoryEnabled: boolean,
    treeOptions: { value: string; label: string; mode: string | null }[],
    legacyOptions: { value: string; label: string }[],
    legacyShow: boolean,
    legacyDefault: string,
    defaultMode: string | null,
  ) => {
    const treeDefault = defaultMode
      ? (treeOptions.find((o) => o.mode === defaultMode)?.value ?? legacyDefault)
      : legacyDefault;
    return {
      visible: treeEnabled ? true : (categoryEnabled ? false : legacyShow),
      options: treeEnabled
        ? [UNSPECIFIED_OPT, ...treeOptions.map((o) => ({ value: o.value, label: o.label }))]
        : legacyOptions,
      defaultValue: treeEnabled ? treeDefault : legacyDefault,
    };
  };
  const narrationField = behaviorField(
    narrationTreeEnabled, narrationCategoryEnabled, narrationTreeOptions,
    [UNSPECIFIED_OPT, { value: 'include', label: 'あり' }, { value: 'exclude', label: 'なし' }],
    scenarioFieldConfig.show_narration, 'unspecified', null,
  );
  const dialogueField = behaviorField(
    dialogueTreeEnabled, dialogueCategoryEnabled, dialogueTreeOptions,
    [UNSPECIFIED_OPT, { value: 'include', label: 'あり' }, { value: 'exclude', label: 'なし' }],
    scenarioFieldConfig.show_dialogue, 'unspecified', null,
  );
  // テロップの既定は「あり（後付け）」(generate_only)。tree 有効時は mode='generate_only' の key。
  const telopField = behaviorField(
    telopTreeEnabled, telopCategoryEnabled, telopTreeOptions,
    [UNSPECIFIED_OPT, { value: 'include', label: 'あり' }, { value: 'generate_only', label: 'あり（後付け）' }, { value: 'exclude', label: 'なし' }],
    scenarioFieldConfig.show_telop, 'generate_only', 'generate_only',
  );
  // モーション (§10.7.5・未決 C): 「ナレーション」「セリフ」を「声 (あり / なし)」1 つに畳む。両方に同じ意味の値を書く
  const motionVoiceValue = deriveMotionVoiceToggle(
    { value: includeNarration, treeEnabled: narrationTreeEnabled, treeOptions: narrationTreeOptions },
    { value: includeDialogue, treeEnabled: dialogueTreeEnabled, treeOptions: dialogueTreeOptions },
  );
  const setMotionVoice = (voice: MotionVoiceToggleValue) => {
    setIncludeNarration(voiceToBehaviorValue(voice, narrationTreeEnabled, narrationTreeOptions));
    setIncludeDialogue(voiceToBehaviorValue(voice, dialogueTreeEnabled, dialogueTreeOptions));
  };
  const showMotionVoiceField = isMotionEngine && (narrationField.visible || dialogueField.visible);
  // 台本取り込み中もテロップの表示方式「後付け」(telop_generate_only=動画には焼き込まず書き出し時に重ねる) は選べる (§4.2)。
  // 有無は台本に従うため、後付けか否かだけを切り替える (後付けでない側は「あり」の option に寄せる)。
  // 取り込み中のテロップは BE が常に後付け (telop_generate_only) に倒す。画面に「後付け」の選択肢は出さない (§4.2)
  // モーショングラフィックスでもテロップの選択肢は据え置き (字幕方式・計画 md §10.8)。以前は切り替え時に「なし」へ倒して
  // いたが、テロップは声の全文の写しとして動画エディタで字幕風に重ねるため、利用者の選択のまま送る (「あり」は backend で後付けに倒す)
  // 想定尺の既定は動画モデルの最大尺に追従する(親で算出した modelAwareDurationDefault)。
  // 黄枠線判定(既定と異なるときに枠線)と未選択時の既定表示に使う。

  // 商品AIプロファイル推奨 (計画書§5.4/§8 P4)。想定尺は modelAwareDurationDefault
  // (動画モデルの最大尺追従) が「秒数昇順」の並びに依存しているため、
  // トーン/映像スタイル (HierarchicalSelect の上位ソート) と異なり並び順は変えず、
  // 一致する option にバッジのみ付与する (表示専用のコピーを作るだけで元の
  // durationOptions の順序・参照には触れない)。
  // 歌モノ (トーン option の metadata.bgm_mode==='lyrics') は想定尺 30 秒以上が必須
  // (docs/features/scenario-song-lyrics-bgm-plan.md §5.1.4)。30 秒未満の option を disable する。
  const durationSelectOptionsWithRecommended = [
    { value: '', label: '指定なし' },
    // 全編モーション (§10.6) は全編を 1 本で作れる 60 秒以内に限る (既に選ばれた 60 秒超は残して警告・送らせない)
    ...(isMotionEngine ? filterDurationOptionsForMotion(durationOptions, duration) : durationOptions).map((opt) => {
      const disabledForLyrics = isLyricsTone && isDurationBelowLyricsMin(opt);
      return {
        ...opt,
        recommended: !!recommendedDuration && opt.value === recommendedDuration.value,
        disabled: disabledForLyrics,
        disabledTooltip: disabledForLyrics ? '歌モノは想定尺 30 秒以上で作成できます' : undefined,
      };
    }),
    // 末尾の「カスタム」: 選ぶと右に秒数入力を出す (customDuration.ts)。値は「45秒」の形で duration に載せる
    { value: CUSTOM_DURATION_OPTION_VALUE, label: 'カスタム' },
  ];

  // 想定尺のカスタム秒数入力。duration が選択肢に無い「N秒」ならカスタム値 (入力欄に秒数を表示)。
  // 「カスタム」を選んだ直後は duration を変えずに空の入力欄を出し、確定 (Enter / フォーカスアウト) で反映する。
  // 空のまま離れたら元の選択に戻る。既存の選択肢と同じ秒数を入れたときはその選択肢を選んだ状態に揃える。
  const [customDurationEditing, setCustomDurationEditing] = useState(false);
  const [customDurationDraft, setCustomDurationDraft] = useState('');
  const isCustomDuration = isCustomDurationValue(durationOptions, duration);
  const showCustomDurationInput = customDurationEditing || isCustomDuration;
  const customDurationSeconds = isCustomDuration ? parseCustomDurationValue(duration) : null;
  const customDurationInputValue = customDurationEditing
    ? customDurationDraft
    : (customDurationSeconds != null ? String(customDurationSeconds) : '');
  const handleDurationSelect = (v: string) => {
    if (v === CUSTOM_DURATION_OPTION_VALUE) {
      setCustomDurationDraft('');
      setCustomDurationEditing(true);
      return;
    }
    setCustomDurationEditing(false);
    onDurationChange(v);
  };
  const commitCustomDuration = () => {
    const seconds = clampCustomDurationSeconds(customDurationDraft);
    setCustomDurationEditing(false);
    if (seconds == null) return;
    const next = customSecondsToDurationValue(durationOptions, seconds);
    if (next !== duration) onDurationChange(next);
  };
  // 現在値の option (カスタム値は metadata.seconds 付きの疑似 option)。選択肢前提の判定に渡す
  const currentDurationOption = resolveDurationOption(durationOptions, duration);

  // 想定尺が動画モデルの1回あたり最大尺を超える場合の分割生成の注意アイコン+ツールチップ。
  // ストーリーボードモード限定 (編集画面で useAutoVideoCardSegmentation が総尺の収まる
  // 数の動画カードを自動配置する)。参考画像/フレームモードは元々シーン単位で1本ずつ
  // 生成するため対象外。旧「Gemini Omni は最大10秒です」確認モーダルの置き換え。
  const durationSeconds = typeof currentDurationOption?.metadata?.seconds === 'number'
    ? (currentDurationOption.metadata.seconds as number)
    : Number(/(\d+)\s*秒/.exec(currentDurationOption?.label ?? '')?.[1] ?? NaN);
  const showDurationSplitNote = videoGenerationMode === 'storyboard'
    && modelMaxDuration > 0
    && Number.isFinite(durationSeconds)
    && durationSeconds > modelMaxDuration;
  // 最低本数 (貪欲詰めの結果はシーン境界次第でこれより増えうるので「以上」表記)
  const durationSplitCount = showDurationSplitNote ? Math.ceil(durationSeconds / modelMaxDuration) : 0;
  const durationSplitModelName = selectedVideoModelDef
    ? `${videoModelGroupLabel(selectedVideoModelDef)} ${videoModelButtonLabel(selectedVideoModelDef)}`.trim()
    : '選択中のAIモデル';

  return (
    <>
          <div className={`accordion-section ${isScenarioSettingsOpen ? 'accordion-section-open' : ''}`}>
            <button
              type="button"
              className="accordion-header"
              onClick={() => setIsScenarioSettingsOpen(!isScenarioSettingsOpen)}
              aria-expanded={isScenarioSettingsOpen}
            >
              <span className="accordion-title">
                <img src="/kindle_hold.svg" alt="" className="accordion-title-icon" />
                <span>シナリオ設定</span>
                <span className="accordion-summary">
                  {scenarioSettingsSummary.map((part, i) => (
                    <span key={i}>
                      {i > 0 && ' / '}
                      {part}
                    </span>
                  ))}
                </span>
              </span>
              <span className={`accordion-icon ${isScenarioSettingsOpen ? 'accordion-icon-open' : ''}`}>
                <svg width="8" height="8" viewBox="0 0 8 8" fill="currentColor" style={{ transition: 'transform 0.2s', transform: isScenarioSettingsOpen ? 'rotate(90deg)' : 'none' }}><path d="M2 0l4 4-4 4z" /></svg>
              </span>
            </button>
            <div className={`accordion-panel ${isScenarioSettingsOpen ? 'accordion-panel-open' : 'accordion-panel-closed'}`}>
              <div className={styles.scenarioFormContainer}>
              {/* 台本取り込み (§4.1)。読み込み後は 1行目の上に全幅カード (プロモーション対象商品の選択済み表示と同じ体裁) */}
              <ScenarioScriptImportCard />
              {/* 1行目: トーン / 想定尺 / AIモデル / シナリオ数 / 推定ポイント (幅に応じて折り返す)。
                  未読み込み時の取り込み入口 (アイコン) はこの行の右上に固定する (ScenarioScriptImport.module.css の .firstRow) */}
              <div
                className={`${styles.scenarioSettingsRow} ${scriptImportStyles.firstRow} ${importedScript ? '' : scriptImportStyles.firstRowIconSpace}`}
              >
                <ScenarioScriptImportEntry />
                {isScriptImport ? scriptFixedField(
                  'script-import-tone', 'トーン', '/content_ink_pen_write.svg', SCRIPT_FOLLOW_LABEL, SCRIPT_IMPORT_FIELD_REASON,
                ) : (
                <div style={{ maxWidth: 200 }}>
                  <HierarchicalSelect
                    label="トーン"
                    icon="/content_ink_pen_write.svg"
                    options={toneHierarchicalOptions}
                    value={tone || 'none'}
                    onChange={(key) => onToneChange(key === 'none' ? '' : key)}
                    recommendedKeys={toneRecommendedKeys}
                    optionIcon={toneOptionIcon}
                  />
                  {/* 全編モーション (§10.5) は歌モノと一体化しない。選択肢から外してあるが、既に選ばれていれば警告 */}
                  {isMotionEngine && isLyricsTone && (
                    <div style={{ fontSize: 12, color: 'rgba(255, 170, 90, 0.9)', marginTop: 4 }}>
                      歌モノのトーンは全編モーショングラフィックスでは使えません。トーンを変えてください
                    </div>
                  )}
                </div>
                )}
                <div className={styles.optionGroup}>
                  <div className="gen-option-group-label" style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                    <img src="/stopwatch.svg" alt="" aria-hidden="true" width={16} height={16} style={{ opacity: 0.85 }} />
                    想定尺
                    <HelpTooltip contentKey="duration" ariaLabel="想定尺のヘルプ" />
                  </div>
                  <div className={styles.durationFieldRow}>
                    <SingleSelectDropdown
                      label="想定尺"
                      options={durationSelectOptionsWithRecommended}
                      value={showCustomDurationInput ? CUSTOM_DURATION_OPTION_VALUE : duration}
                      onChange={handleDurationSelect}
                      defaultValue={modelAwareDurationDefault}
                      labelOnly
                      fitContent
                      alwaysShowSelected
                      className={`${styles.dropdownFit} ${styles.scenarioDropdown}`}
                      dropdownClassName={styles.dropdownNoWrap}
                    />
                    {showCustomDurationInput && (
                      <label className={styles.durationCustomField} data-testid="duration-custom-field">
                        <input
                          type="number"
                          inputMode="numeric"
                          className={styles.durationCustomInput}
                          aria-label="想定尺のカスタム秒数"
                          title={`${CUSTOM_DURATION_MIN_SECONDS}〜${CUSTOM_DURATION_MAX_SECONDS}秒の整数`}
                          placeholder={`${CUSTOM_DURATION_MIN_SECONDS}〜${CUSTOM_DURATION_MAX_SECONDS}`}
                          min={CUSTOM_DURATION_MIN_SECONDS}
                          max={CUSTOM_DURATION_MAX_SECONDS}
                          step={1}
                          value={customDurationInputValue}
                          autoFocus={customDurationEditing && customDurationDraft === ''}
                          onFocus={() => {
                            if (!customDurationEditing) {
                              setCustomDurationDraft(customDurationInputValue);
                              setCustomDurationEditing(true);
                            }
                          }}
                          onChange={(e) => {
                            setCustomDurationDraft(e.target.value);
                            if (!customDurationEditing) setCustomDurationEditing(true);
                          }}
                          onBlur={commitCustomDuration}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              (e.target as HTMLInputElement).blur();
                            } else if (e.key === 'Escape') {
                              e.preventDefault();
                              setCustomDurationDraft('');
                              setCustomDurationEditing(false);
                            }
                          }}
                        />
                        <span className={styles.durationCustomSuffix}>秒</span>
                      </label>
                    )}
                    {/* 想定尺 > モデル最大尺のときの分割生成の注意アイコン (ホバーで詳細) */}
                    {showDurationSplitNote && (
                      <Tooltip
                        content={`${durationSplitModelName}は動画1本あたり最大${modelMaxDuration}秒までしか生成できないため、${durationSplitCount}本以上の動画を連結して出力します。`}
                        className={styles.durationSplitIconWrap}
                      >
                        <svg
                          className={styles.durationSplitIcon}
                          width="15" height="15" viewBox="0 0 24 24" fill="none"
                          stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                          role="img" aria-label="分割生成の注意"
                        >
                          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                          <line x1="12" y1="9" x2="12" y2="13" />
                          <line x1="12" y1="17" x2="12.01" y2="17" />
                        </svg>
                      </Tooltip>
                    )}
                  </div>
                  {isMotionEngine && isDurationOverMotionWholeMax(currentDurationOption) && (
                    <div style={{ fontSize: 12, color: 'rgba(255, 170, 90, 0.9)', marginTop: 4, maxWidth: 260 }}>
                      {MOTION_DURATION_TOO_LONG_MESSAGE}
                    </div>
                  )}
                </div>
                <div className={styles.optionGroup}>
                  <div className="gen-option-group-label" style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                    <img src="/reward_stars_2.svg" alt="" aria-hidden="true" width={16} height={16} style={{ opacity: 0.85 }} />
                    シナリオ生成AIモデル
                  </div>
                  {/* 動画モデル選択と共通のポータルドロップダウン (ModelDropdownMenu)。
                      トリガーは隣接する SingleSelectDropdown と同じ見た目に揃える */}
                  <button
                    type="button"
                    className={`${msStyles.button} ${scenarioModel !== DEFAULT_SCENARIO_MODEL ? msStyles.active : ''} ${styles.scenarioModelTrigger}`}
                    onClick={(e) => scenarioModelDropdown.open('scenario-model', e.currentTarget)}
                  >
                    <span className={msStyles.buttonText}>
                      <span className={msStyles.buttonLabel}>
                        {scenarioModel === SCENARIO_MODEL_RANDOM || !currentScenarioModelDef ? (
                          <>
                            ランダム
                            {randomRateBadge !== undefined && (
                              <span className={msStyles.optionBadge}>{randomRateBadge}</span>
                            )}
                          </>
                        ) : (
                          <>
                            <img src={currentScenarioModelDef.icon} alt="" className={msStyles.optionIcon} />
                            <span className={msStyles.optionLabelGroup}>
                              <span>{currentScenarioModelDef.label}</span>
                              <span className={msStyles.optionSublabel}>{currentScenarioModelDef.sublabel}</span>
                            </span>
                            {currentScenarioModelDef.rate !== null && (
                              <span className={msStyles.optionBadge}>{currentScenarioModelDef.rate}</span>
                            )}
                          </>
                        )}
                      </span>
                    </span>
                    <span className={msStyles.arrow}>
                      <ChevronIcon open={scenarioModelDropdown.openId === 'scenario-model'} />
                    </span>
                  </button>
                  <ScenarioModelDropdownPortal
                    dropdown={scenarioModelDropdown}
                    scenarioModelDefs={scenarioModelDefs}
                    randomRateBadge={randomRateBadge}
                    scenarioModel={scenarioModel}
                    setScenarioModel={setScenarioModel}
                  />
                </div>
                <div className={styles.optionGroup} data-testid="scenario-count-field">
                  <div className="gen-option-group-label">シナリオ数</div>
                  {/* 台本取り込みは1案固定 (§4.2)。非活性のトリガーはホバーを受けないためラッパーで理由を出す */}
                  {isScriptImport ? (
                    <Tooltip content={SCRIPT_IMPORT_COUNT_REASON}>
                      <div style={{ pointerEvents: 'none' }}>
                        <SingleSelectDropdown
                          label="シナリオ数"
                          options={[{ value: '1', label: '1案' }]}
                          value="1"
                          onChange={() => {}}
                          disabled
                          labelOnly
                          fitContent
                          alwaysShowSelected
                          className={`${styles.dropdownFit} ${styles.scenarioDropdown}`}
                          dropdownClassName={styles.dropdownNoWrap}
                        />
                      </div>
                    </Tooltip>
                  ) : (
                  /* モーショングラフィックスはBGM先行フローが1案に固定するため選択肢も1案だけ (ScenarioGeneratorのeffectが強制+トースト) */
                  <SingleSelectDropdown
                    label="シナリオ数"
                    options={filterScenarioCountOptionsForMotion(scenarioCountOptions, !!isMotionEngine).map((count) => ({ value: String(count), label: `${count}案` }))}
                    value={String(scenarioCount)}
                    onChange={(v) => setScenarioCount(Number(v))}
                    disabled={!!isMotionEngine}
                    defaultValue="2"
                    labelOnly
                    fitContent
                    alwaysShowSelected
                    className={`${styles.dropdownFit} ${styles.scenarioDropdown}`}
                    dropdownClassName={styles.dropdownNoWrap}
                  />
                  )}
                </div>
                <div className={`${styles.optionGroup} ${styles.estimatedCreditsGroup}`}>
                  <div className="gen-option-group-label" aria-hidden="true">&nbsp;</div>
                  <div className={styles.estimatedCreditsValue}>
                    {scenarioModel === SCENARIO_MODEL_RANDOM
                      ? (randomCreditText ? <>{randomCreditText}<small>pt</small></> : '—')
                      : (selectedScenarioModelRate !== null
                          ? <>{selectedScenarioModelRate * displayScenarioCount}<small>pt</small></>
                          : '—')}
                  </div>
                </div>
              </div>
              {/* 2行目: ナレーション / セリフ / テロップ / 映像スタイル(placement=primary 時) */}
              <div className={styles.scenarioSettingsRow}>
                {showMotionVoiceField && (
                  <div className={styles.optionGroup} data-testid="motion-voice-toggle">
                    <div className="gen-option-group-label" style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                      <img src="/microphone_1.svg" alt="" aria-hidden="true" className={styles.optionLabelIcon} />
                      ナレーション
                    </div>
                    <SingleSelectDropdown
                      label="ナレーション"
                      options={MOTION_VOICE_TOGGLE_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
                      value={motionVoiceValue}
                      onChange={(v) => setMotionVoice(v as MotionVoiceToggleValue)}
                      defaultValue="unspecified"
                      labelOnly
                      fitContent
                      alwaysShowSelected
                      className={`${styles.dropdownFit} ${styles.scenarioDropdown}`}
                      dropdownClassName={styles.dropdownNoWrap}
                    />
                  </div>
                )}
                {/* 台本取り込み中: ナレーション / セリフ / テロップの有無は台本に従う (§4.2) */}
                {isScriptImport && narrationField.visible && scriptFixedField(
                  'script-import-narration', 'ナレーション', '/microphone_1.svg', SCRIPT_FOLLOW_PENDING_LABEL, SCRIPT_IMPORT_FIELD_REASON,
                )}
                {isScriptImport && dialogueField.visible && scriptFixedField(
                  'script-import-dialogue', 'セリフ', '/messages_people_person_bubble_circle_1.svg', SCRIPT_FOLLOW_PENDING_LABEL, SCRIPT_IMPORT_FIELD_REASON,
                )}
                {isScriptImport && telopField.visible && scriptFixedField(
                  'script-import-telop', 'テロップ', '/subtitles.svg', SCRIPT_FOLLOW_PENDING_LABEL, SCRIPT_IMPORT_FIELD_REASON,
                )}
                {!isScriptImport && !isMotionEngine && narrationField.visible && (
                  <div className={styles.optionGroup}>
                    <div className="gen-option-group-label" style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                      <img src="/microphone_1.svg" alt="" aria-hidden="true" className={styles.optionLabelIcon} />
                      ナレーション
                    </div>
                    <SingleSelectDropdown
                      label="ナレーション"
                      options={narrationField.options}
                      value={includeNarration}
                      onChange={(v) => setIncludeNarration(v)}
                      defaultValue={narrationField.defaultValue}
                      labelOnly
                      fitContent
                      alwaysShowSelected
                      className={`${styles.dropdownFit} ${styles.scenarioDropdown}`}
                      dropdownClassName={styles.dropdownNoWrap}
                    />
                  </div>
                )}
                {!isScriptImport && !isMotionEngine && dialogueField.visible && (
                  <div className={styles.optionGroup}>
                    <div className="gen-option-group-label" style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                      <img src="/messages_people_person_bubble_circle_1.svg" alt="" aria-hidden="true" className={styles.optionLabelIcon} />
                      セリフ
                    </div>
                    <SingleSelectDropdown
                      label="セリフ"
                      options={dialogueField.options}
                      value={includeDialogue}
                      onChange={(v) => setIncludeDialogue(v)}
                      defaultValue={dialogueField.defaultValue}
                      labelOnly
                      fitContent
                      alwaysShowSelected
                      className={`${styles.dropdownFit} ${styles.scenarioDropdown}`}
                      dropdownClassName={styles.dropdownNoWrap}
                    />
                  </div>
                )}
                {!isScriptImport && telopField.visible && (
                  <div className={styles.optionGroup}>
                    <div className="gen-option-group-label" style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                      <img src="/subtitles.svg" alt="" aria-hidden="true" className={styles.optionLabelIcon} />
                      テロップ
                    </div>
                    <SingleSelectDropdown
                      label="テロップ"
                      options={telopField.options}
                      value={includeTelop}
                      onChange={(v) => setIncludeTelop(v)}
                      defaultValue={telopField.defaultValue}
                      labelOnly
                      fitContent
                      alwaysShowSelected
                      className={`${styles.dropdownFit} ${styles.scenarioDropdown}`}
                      dropdownClassName={styles.dropdownNoWrap}
                    />
                  </div>
                )}
                {/* 映像スタイル: フィールドの placement が primary のとき主表示エリア(テロップの右)に出す。 */}
                {visualStyleFieldPlacement === 'primary' && visualStyleBlock}
                {motionGraphicsFieldPlacement === 'primary' && motionGraphicsBlock}
                {shootingMethodFieldPlacement === 'primary' && shootingMethodBlock}
                {dialectFieldPlacement === 'primary' && dialectBlock}
                {targetGenderFieldPlacement === 'primary' && targetGenderBlock}
                {targetAgeFieldPlacement === 'primary' && targetAgeBlock}
              </div>
              </div>
              {/* 詳細設定: 映像スタイル・撮影方法・方言・ターゲット (placement=advanced のもの) / 追加プロンプト (デフォルトはコラプス) */}
              <div className={styles.scenarioAdvancedSection}>
                <button
                  type="button"
                  onClick={() => setIsScenarioAdvancedOpen(!isScenarioAdvancedOpen)}
                  aria-expanded={isScenarioAdvancedOpen}
                  className={styles.scenarioAdvancedToggle}
                >
                  <svg
                    width="8"
                    height="8"
                    viewBox="0 0 8 8"
                    fill="currentColor"
                    style={{ transition: 'transform 0.2s', transform: isScenarioAdvancedOpen ? 'rotate(90deg)' : 'none' }}
                  >
                    <path d="M2 0l4 4-4 4z" />
                  </svg>
                  <span>詳細設定</span>
                </button>
                {isScenarioAdvancedOpen && (
                <div className={styles.scenarioAdvancedBody}>
                {/* 映像スタイル */}
                <div className={styles.scenarioSettingsRow}>
                {/* 映像スタイル。フィールドの placement が advanced のときのみ詳細設定内に
                    表示する (primary のときは主表示エリアに出す)。 */}
                {visualStyleFieldPlacement !== 'primary' && visualStyleBlock}
                {motionGraphicsFieldPlacement === 'advanced' && motionGraphicsBlock}
                {shootingMethodFieldPlacement === 'advanced' && shootingMethodBlock}
                {dialectFieldPlacement === 'advanced' && dialectBlock}
                {targetGenderFieldPlacement === 'advanced' && targetGenderBlock}
                {targetAgeFieldPlacement === 'advanced' && targetAgeBlock}
                </div>
                {/* 追加プロンプト (履歴付き。静止画の「追加プロンプト」と同じ UI) */}
                <div className="prompt-history-container" style={{ width: '100%' }}>
                  <div className="prompt-label-row">
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <img src="/content_pen_write.svg" alt="" aria-hidden="true" width={16} height={16} style={{ opacity: 0.85 }} />
                      <span className="gen-label-text">シナリオ生成の追加プロンプト</span>
                    </span>
                    <button
                      type="button"
                      className="prompt-history-toggle"
                      onClick={handleToggleAdditionalPromptHistory}
                    >
                      <span aria-hidden="true" style={{ display: 'inline-block', width: 12, height: 12, marginRight: 4, verticalAlign: '-2px', backgroundColor: 'currentColor', WebkitMaskImage: 'url(/time_clock_circle.svg)', maskImage: 'url(/time_clock_circle.svg)', WebkitMaskSize: 'contain', maskSize: 'contain', WebkitMaskRepeat: 'no-repeat', maskRepeat: 'no-repeat', WebkitMaskPosition: 'center', maskPosition: 'center' }} />
                      履歴
                    </button>
                  </div>
                  <textarea
                    className="gen-textarea"
                    value={additionalPrompt}
                    onChange={(e) => setAdditionalPrompt(e.target.value)}
                    placeholder="（例）クリスマスをテーマに。別れ話をネタに。10月2日からのセールをテーマに。"
                    rows={3}
                    style={{ minHeight: '60px' }}
                  />
                  <PromptHistoryModal
                    isOpen={showAdditionalPromptHistory}
                    onClose={() => setShowAdditionalPromptHistory(false)}
                    scope="scenario_additional"
                    storageKey={PROMPT_HISTORY_KEYS.SCENARIO_ADDITIONAL_PROMPT}
                    onSelect={handleSelectAdditionalPromptHistory}
                    title="シナリオ生成の追加プロンプトの履歴"
                  />
                </div>
                </div>
                )}
              </div>
              <div className={styles.accordionPanelFooter}>
                <Tooltip content="閉じる">
                  <button type="button" className={styles.iconButton} onClick={() => setIsScenarioSettingsOpen(false)}>
                    <img src="/move_collapse_vertical.svg" alt="閉じる" style={{ width: '16px', height: '16px' }} />
                  </button>
                </Tooltip>
              </div>
            </div>
          </div>
    </>
  );
}
